import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ChimeMeetingService } from '@app/chat/services/chime-meeting.service';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { BrandingService } from '@app/core/services/branding.service';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import { TranslateModule } from '@ngx-translate/core';
import { ConsoleLogger, DefaultDeviceController, LogLevel } from 'amazon-chime-sdk-js';
import { finalize, firstValueFrom } from 'rxjs';

import { VideoSettings } from '../../models/settings.models';
import { SettingsPage } from '../../models/settings-page';
import { SettingsService } from '../../services/settings.service';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    BrandingColorDirective,
    MatIconModule,
    CommonModule,
    BrandingColorDirective,
    MatButtonModule,
    TranslateModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatInputModule,
  ],
  selector: 'app-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
})
export class VideoComponent extends SettingsPage implements OnInit, OnDestroy {
  @ViewChild('video', { static: false })
  private webcam: ElementRef;
  protected activeCameraList: MediaDeviceInfo[] = [];
  protected selectedCamera?: MediaDeviceInfo;
  protected cameraError = false;
  protected form: FormGroup;
  private deviceController: DefaultDeviceController;
  protected readonly BrandingField = BrandingField;
  protected defaultBrandingSettings = defaultBrandingSettings;

  get mirrorLocalVideo(): boolean {
    return this.form.controls['mirrorLocalVideo'].value;
  }

  get cameraDevice(): string {
    return this.form.controls['cameraDevice'].value;
  }

  set cameraDevice(deviceId: string) {
    this.form.controls['cameraDevice'].setValue(deviceId);
  }

  get blurBackground(): boolean {
    return this.form.controls['blurBackground'].value;
  }

  set blurBackground(blurBackground: boolean) {
    this.form.controls['blurBackground'].setValue(blurBackground);
  }

  constructor(
    private settingsService: SettingsService,
    private formBuilder: FormBuilder,
    private chimeMeetingService: ChimeMeetingService,
    private appConfigService: AppConfigService,
    private appTranslateService: AppTranslateService,
    protected brandingService: BrandingService
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    // Initialize form with current settings
    this.form = this.createFormGroup(this.settingsService.videoSettings);

    // Scan devices and start camera once
    await this.scanDevices();
    await this.tryStartCameraStream();
  }

  private createFormGroup(settings: VideoSettings): FormGroup {
    return this.formBuilder.group({
      cameraDevice: [settings.cameraDevice],
      mirrorLocalVideo: [settings.mirrorLocalVideo],
      blurBackground: [settings.blurBackground],
      cameraOn: [settings.cameraOn],
    });
  }

  protected async scanDevices() {
    this.activeCameraList = [];
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return;
    }

    // Request camera permission first - enumerateDevices only returns full device info after permission granted
    let permissionStream: MediaStream | null = null;
    try {
      permissionStream = await navigator.mediaDevices.getUserMedia({ video: true });
    } catch (error) {
      console.log('Failed to get camera permission:', error);
      // Continue anyway - user may have denied permission or no camera available
    }

    try {
      const mediaDevices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices();
      if (mediaDevices && mediaDevices.length > 0) {
        this.activeCameraList = mediaDevices.filter(
          (camera: MediaDeviceInfo) => camera.deviceId && camera.kind === 'videoinput'
        );
        if (this.activeCameraList.length > 0) {
          if (this.cameraDevice) {
            this.selectedCamera = this.activeCameraList.find(
              (camera: MediaDeviceInfo) => camera.deviceId === this.cameraDevice
            );
            if (!this.selectedCamera) {
              this.selectFirstCamera();
            }
          } else {
            this.selectFirstCamera();
          }
        }
      }
    } finally {
      // Stop the permission stream - we only needed it to get permission
      if (permissionStream) {
        permissionStream.getTracks().forEach((track) => track.stop());
      }
    }
  }

  private selectFirstCamera() {
    this.selectedCamera = this.activeCameraList[0];
    this.cameraDevice = this.selectedCamera.deviceId;
  }

  private async tryStartCameraStream() {
    // If no cameras are available, show error immediately
    if (this.activeCameraList.length === 0) {
      console.log('No cameras available');
      this.cameraError = true;
      return;
    }

    try {
      this.cameraError = false;
      await this.startCameraStream();
    } catch (error) {
      console.log('start camera error: ', error);

      // If the selected camera failed, try falling back to the first available camera
      if (this.activeCameraList.length > 0 && this.selectedCamera?.deviceId !== this.activeCameraList[0].deviceId) {
        console.log('Falling back to first available camera');
        this.selectFirstCamera();
        try {
          await this.startCameraStream();
          return;
        } catch (fallbackError) {
          console.log('fallback camera error: ', fallbackError);
        }
      }

      this.cameraError = true;
    }
  }

  private async startCameraStream() {
    // Stop and clean up previous camera stream before starting new one
    await this.stopCameraStream();

    const logger = new ConsoleLogger('uc-web-v2 Video Preferences', LogLevel.INFO);
    this.deviceController = new DefaultDeviceController(logger);

    // Only use selectedCamera (MediaDeviceInfo), not the string deviceId from form
    // This prevents using stale/invalid deviceIds that no longer exist
    if (!this.selectedCamera) {
      throw new Error(this.appTranslateService.instant('VIDEO_SETTINGS_PREFERENCES.NOT_SUPPORTED_YOUR_CAMERA'));
    }

    const transformDevice = await this.chimeMeetingService.getVideoTransformDevice(
      this.selectedCamera,
      this.blurBackground
    );

    const mediaStream = await this.deviceController.startVideoInput(transformDevice);
    if (!mediaStream) {
      throw new Error(this.appTranslateService.instant('VIDEO_SETTINGS_PREFERENCES.NOT_SUPPORTED_YOUR_CAMERA'));
    }

    this.bindStreamToVideoElement(mediaStream);
  }

  private bindStreamToVideoElement(stream: MediaStream) {
    const videoElement = this.webcam.nativeElement;
    videoElement.srcObject = stream;
    videoElement.muted = videoElement.autoplay = videoElement.playsInline = true;
  }

  private async stopCameraStream() {
    // Clear video element
    if (this.webcam?.nativeElement) {
      const videoElement = this.webcam.nativeElement;
      videoElement.pause();
    }

    // Stop device controller
    if (this.deviceController) {
      await this.deviceController.stopVideoInput();
    }
  }

  protected async onCameraChanged(selectedCamera: MediaDeviceInfo) {
    this.disabled = false;
    this.cameraDevice = selectedCamera.deviceId;
    this.selectedCamera = selectedCamera;
    await this.tryStartCameraStream();
  }

  public async save() {
    this.loading = true;
    await firstValueFrom(
      this.appConfigService.saveVideoSettings(this.form.value).pipe(finalize(() => (this.loading = false)))
    );
  }

  protected onFormChange() {
    this.disabled = false;
  }

  protected async onBackgroundStatusChange(event: MatCheckboxChange) {
    this.disabled = false;
    this.blurBackground = event.checked;
    await this.tryStartCameraStream();
  }

  async ngOnDestroy() {
    // Clear video element immediately
    if (this.webcam?.nativeElement) {
      this.webcam.nativeElement.srcObject = null;
    }

    if (this.deviceController) {
      await this.deviceController
        .stopVideoInput()
        .catch((err) => console.error('Error stopping device controller:', err));
    }
  }
}
